import { SHIPMENT_EDIT_FIELD_LABELS } from './shipmentEditRequest.js'

export const REVIEW_FIELD_GROUPS = [
  {
    id: 'payment',
    title: 'Payment',
    fields: ['mode_of_payment', 'payment_mode', 'shipment_client_id'],
  },
  {
    id: 'invoice',
    title: 'Invoice',
    fields: ['invoice_number', 'invoice_value'],
  },
  {
    id: 'destination',
    title: 'Destination',
    fields: ['consignee_location', 'receiver_location', 'receiver_location_hub_id'],
  },
  {
    id: 'consignor',
    title: 'Consignor',
    fields: ['sender_name', 'sender_contact', 'sender_email', 'sender_address'],
  },
  {
    id: 'consignee',
    title: 'Consignee',
    fields: ['receiver_name', 'receiver_contact', 'receiver_email', 'receiver_address'],
  },
]

export const formatPaymentStatusLabel = (value) => {
  const raw = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_')
  if (!raw) return '—'
  if (raw === 'to_pay') return 'To Pay'
  if (raw === 'paid') return 'Paid'
  if (raw === 'credit') return 'Credit'
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export const formatPaymentModeLabel = (value) => {
  const text = String(value || '').trim()
  if (!text) return '—'
  return text.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export const formatInrAmount = (value) => {
  if (value == null || value === '') return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(num)
}

const parcelComparable = (row) => ({
  sr_no: row?.sr_no ?? null,
  package_name: String(row?.package_name || '').trim(),
  article_type: String(row?.article_type || row?.package_name || '').trim().toUpperCase(),
  article_amount: Number(row?.article_amount ?? 0),
  actual_weight: Number(row?.actual_weight ?? 0),
  chargeable_weight: Number(row?.chargeable_weight ?? row?.actual_weight ?? 0),
  qty: Number(row?.qty ?? 1),
  length_cm: row?.length_cm ?? null,
  width_cm: row?.width_cm ?? null,
  height_cm: row?.height_cm ?? null,
  cbm: row?.cbm ?? null,
  volumetric_weight: row?.volumetric_weight ?? null,
})

export const normalizeParcelSnapshot = (parcels) =>
  (Array.isArray(parcels) ? parcels : []).map((row, index) =>
    parcelComparable({ ...row, sr_no: row?.sr_no ?? index + 1 }),
  )

export const diffParcelSnapshots = (current, proposed) => {
  const before = normalizeParcelSnapshot(current)
  const after = normalizeParcelSnapshot(proposed)
  const maxLen = Math.max(before.length, after.length)
  const rows = []
  for (let i = 0; i < maxLen; i += 1) {
    const left = before[i]
    const right = after[i]
    let status = 'unchanged'
    if (!left && right) status = 'added'
    else if (left && !right) status = 'removed'
    else if (left && right) {
      status =
        JSON.stringify(left) === JSON.stringify(right) ? 'unchanged' : 'changed'
    }
    rows.push({
      index: i + 1,
      status,
      current: left || null,
      proposed: right || null,
    })
  }
  return rows
}

export const parcelHasDimensions = (row) =>
  [row?.length_cm, row?.width_cm, row?.height_cm].some(
    (v) => v != null && v !== '' && Number(v) > 0,
  )

export const describeParcelRow = (row, { cargoHint = false } = {}) => {
  if (!row) return '—'
  const type = row.article_type || row.package_name || 'Article'
  const qty = row.qty ?? 1
  const parts = [`${type}`, `Qty ${qty}`]

  if (cargoHint || parcelHasDimensions(row)) {
    const dims = [row.length_cm, row.width_cm, row.height_cm]
      .filter((v) => v != null && v !== '')
      .join(' × ')
    if (dims) parts.push(`${dims} cm`)
    const wt = row.chargeable_weight || row.actual_weight
    if (wt) parts.push(`${wt} kg chargeable`)
    else if (row.actual_weight) parts.push(`${row.actual_weight} kg`)
    if (row.cbm) parts.push(`CBM ${row.cbm}`)
  } else {
    if (row.article_amount > 0) parts.push(`Rate ${formatInrAmount(row.article_amount)}`)
    const wt = row.chargeable_weight || row.actual_weight
    if (wt > 0) parts.push(`${wt} kg`)
  }
  return parts.join(' · ')
}

export const dedupeDisplayFields = (fields) => {
  const set = new Set(fields || [])
  if (set.has('consignee_location') && set.has('receiver_location')) {
    set.delete('receiver_location')
  }
  return [...set]
}

export const resolveInstrumentFromShipment = (shipment) => {
  if (!shipment) return null
  if (shipment.payment_mode) return shipment.payment_mode
  const history = Array.isArray(shipment.payment_history) ? shipment.payment_history : []
  const booking = history.find(
    (row) => row?.payment_mode && String(row.type || '').toLowerCase() === 'booking',
  )
  const row = booking || history.find((entry) => entry?.payment_mode)
  return row?.payment_mode || null
}

export const formatReviewFieldValue = (key, value, context = {}) => {
  if (value == null || value === '') return '—'
  if (key === 'mode_of_payment') return formatPaymentStatusLabel(value)
  if (key === 'payment_mode') return formatPaymentModeLabel(value)
  if (key === 'invoice_value') return formatInrAmount(value)
  if (key === 'receiver_location_hub_id') {
    const hubMap = context.hubNamesById || {}
    const id = String(value)
    return hubMap[id] || `Branch #${value}`
  }
  if (key === 'shipment_client_id') {
    const clientMap = context.creditClientNamesById || {}
    const id = String(value)
    return clientMap[id] || `Credit client #${value}`
  }
  if (key === 'parcels' && Array.isArray(value)) {
    return value.length ? `${value.length} article(s)` : '—'
  }
  return String(value).trim() || '—'
}

export const groupsWithChanges = (changedFields) => {
  const changed = new Set(changedFields || [])
  return REVIEW_FIELD_GROUPS.map((group) => ({
    ...group,
    fields: group.fields.filter((field) => changed.has(field)),
  })).filter((group) => group.fields.length > 0)
}

export const fieldLabel = (key) => SHIPMENT_EDIT_FIELD_LABELS[key] || key

export const parcelDiffSummary = (current, proposed) => {
  const rows = diffParcelSnapshots(current, proposed)
  const added = rows.filter((r) => r.status === 'added').length
  const removed = rows.filter((r) => r.status === 'removed').length
  const changed = rows.filter((r) => r.status === 'changed').length
  return { rows, added, removed, changed, totalBefore: normalizeParcelSnapshot(current).length, totalAfter: normalizeParcelSnapshot(proposed).length }
}
